import { OfflineSqliteDatabase, rows, type SqlJsDatabase } from "./sqlite";
import type { CreateSaleInput, OutboxOperation, ServerChange, StoredSale } from "./types";

const uuid = (): string => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const asMoney = (value: number, name: string): number => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${name} must be a non-negative integer in minor units`);
  return value;
};

export class OfflineStore {
  constructor(private readonly database: OfflineSqliteDatabase) {}

  /** Records the complete sale and its sync operation in one durable SQLite commit. */
  async createSale(input: CreateSaleInput): Promise<StoredSale> {
    if (!input.items.length) throw new Error("A sale must contain at least one item");
    if (!/^[A-Z]{3}$/.test(input.currency)) throw new Error("currency must be a three-letter uppercase code");
    const createdAt = input.createdAt ?? new Date().toISOString();
    const items = input.items.map((item) => ({
      ...item,
      id: item.id ?? uuid(),
      quantity: item.quantity,
      unitPriceMinor: asMoney(item.unitPriceMinor, "unitPriceMinor"),
      taxMinor: asMoney(item.taxMinor ?? 0, "taxMinor"),
    }));
    for (const item of items) if (!Number.isFinite(item.quantity) || item.quantity <= 0) throw new Error("quantity must be positive");
    const payments = input.payments.map((payment) => ({ ...payment, id: payment.id ?? uuid(), amountMinor: asMoney(payment.amountMinor, "amountMinor") }));
    const subtotalMinor = items.reduce((sum, item) => {
      const next = sum + Math.round(item.quantity * item.unitPriceMinor);
      if (!Number.isSafeInteger(next)) throw new Error("sale total exceeds safe integer range");
      return next;
    }, 0);
    const taxMinor = items.reduce((sum, item) => sum + item.taxMinor, 0);
    const totalMinor = subtotalMinor + taxMinor;
    if (payments.reduce((sum, payment) => sum + payment.amountMinor, 0) < totalMinor) throw new Error("payments do not cover the sale total");
    const sale: StoredSale = { ...input, id: input.id ?? uuid(), createdAt, items, payments, subtotalMinor, taxMinor, totalMinor } as StoredSale;
    const operationId = uuid();
    const receiptPayload = {
      receiptNumber: `${sale.terminalId}-${sale.id.slice(0, 8)}`,
      customerId: null,
      type: "sale",
      currency: sale.currency,
      subtotalMinor,
      taxMinor,
      totalMinor,
      paymentMethod: payments[0]?.method ?? "cash",
      cloudlinkPollId: payments[0]?.method === "card" ? payments[0]?.reference ?? null : null,
      cardReceipt: payments[0]?.method === "card" ? payments[0]?.cardReceipt ?? null : null,
      relatedReceiptId: null,
      completedAt: createdAt,
      lines: items.map((item) => ({
        productId: item.productId ?? null,
        description: item.name,
        quantityMilli: Math.round(item.quantity * 1000),
        unitPriceMinor: item.unitPriceMinor,
        taxRateBps: item.taxMinor > 0 ? 2000 : 0,
        lineTotalMinor: Math.round(item.quantity * item.unitPriceMinor) + item.taxMinor,
      })),
    };

    await this.database.transaction((db) => {
      db.run("INSERT INTO sales(id,terminal_id,employee_id,currency,subtotal_minor,tax_minor,total_minor,created_at) VALUES(?,?,?,?,?,?,?,?)", [sale.id, sale.terminalId, sale.employeeId ?? null, sale.currency, subtotalMinor, taxMinor, totalMinor, createdAt]);
      for (const item of items) db.run("INSERT INTO sale_items(id,sale_id,product_id,name,quantity_milli,unit_price_minor,tax_minor) VALUES(?,?,?,?,?,?,?)", [item.id, sale.id, item.productId ?? null, item.name, Math.round(item.quantity * 1000), item.unitPriceMinor, item.taxMinor]);
      for (const payment of payments) db.run(`INSERT INTO payments
        (id,sale_id,method,amount_minor,reference,card_scheme,card_last4,authorization_code,retrieval_reference_number,uti,terminal_id,merchant_id,receipt_text)
        VALUES(?,?,?,?,?,?,?,?,?,?,?,?,?)`, [payment.id, sale.id, payment.method, payment.amountMinor, payment.reference ?? null,
        payment.cardReceipt?.cardScheme ?? null, payment.cardReceipt?.cardLast4 ?? null,
        payment.cardReceipt?.authorizationCode ?? null, payment.cardReceipt?.retrievalReferenceNumber ?? null,
        payment.cardReceipt?.uti ?? null, payment.cardReceipt?.terminalId ?? null, payment.cardReceipt?.merchantId ?? null,
        payment.cardReceipt?.receiptText ?? null]);
      db.run("INSERT INTO outbox(id,entity_type,entity_id,operation,payload,created_at) VALUES(?,?,?,?,?,?)", [operationId, "receipt", sale.id, "upsert", JSON.stringify(receiptPayload), createdAt]);
    });
    return sale;
  }

  pendingOperations(limit = 100): OutboxOperation[] {
    const records = rows(this.database.raw, "SELECT id,entity_type,entity_id,operation,payload,created_at,attempts FROM outbox WHERE blocked=0 ORDER BY created_at,id LIMIT ?", [limit]);
    return records.map((row) => ({ id: String(row.id), entityType: String(row.entity_type), entityId: String(row.entity_id), operation: String(row.operation), payload: JSON.parse(String(row.payload)), createdAt: String(row.created_at), attempts: Number(row.attempts) }));
  }

  listSales(): Array<{ id: string; createdAt: string; subtotalMinor: number; taxMinor: number; totalMinor: number; itemCount: number;
    items: Array<{ productId?: string; name: string; quantity: number; unitPriceMinor: number; lineTotalMinor: number }>;
    payment: string; synced: boolean;
    cardReceipt?: { cardScheme?: string; cardLast4?: string; authorizationCode?: string; retrievalReferenceNumber?: string;
      uti?: string; terminalId?: string; merchantId?: string; receiptText?: string } }> {
    return rows(this.database.raw, `SELECT s.id,s.created_at,s.subtotal_minor,s.tax_minor,s.total_minor,s.synced_at,
      (SELECT COALESCE(SUM(quantity_milli),0) FROM sale_items WHERE sale_id=s.id) AS item_count_milli,
      p.method AS payment,p.card_scheme,p.card_last4,p.authorization_code,p.retrieval_reference_number,p.uti,p.terminal_id,p.merchant_id,p.receipt_text
      FROM sales s LEFT JOIN payments p ON p.id=(SELECT id FROM payments WHERE sale_id=s.id ORDER BY id LIMIT 1)
      ORDER BY s.created_at DESC`).map((row) => {
        const cardReceipt = Object.fromEntries([
          ["cardScheme", row.card_scheme], ["cardLast4", row.card_last4], ["authorizationCode", row.authorization_code],
          ["retrievalReferenceNumber", row.retrieval_reference_number], ["uti", row.uti],
          ["terminalId", row.terminal_id], ["merchantId", row.merchant_id], ["receiptText", row.receipt_text],
        ].filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0));
        return {
          id: String(row.id), createdAt: String(row.created_at), subtotalMinor: Number(row.subtotal_minor),
          taxMinor: Number(row.tax_minor), totalMinor: Number(row.total_minor),
          itemCount: Number(row.item_count_milli) / 1000, payment: String(row.payment ?? "cash"), synced: Boolean(row.synced_at),
          items: rows(this.database.raw, `SELECT product_id,name,quantity_milli,unit_price_minor,tax_minor
            FROM sale_items WHERE sale_id=? ORDER BY rowid`, [row.id]).map((item) => {
            const quantity = Number(item.quantity_milli) / 1000;
            const netTotalMinor = Math.round(quantity * Number(item.unit_price_minor));
            const lineTotalMinor = netTotalMinor + Number(item.tax_minor);
            return {
              productId: typeof item.product_id === "string" ? item.product_id : undefined,
              name: String(item.name), quantity,
              unitPriceMinor: quantity > 0 ? Math.round(lineTotalMinor / quantity) : 0,
              lineTotalMinor,
            };
          }),
          cardReceipt: Object.keys(cardReceipt).length ? cardReceipt : undefined,
        };
      });
  }

  pendingCount(): number {
    return Number(rows(this.database.raw, "SELECT COUNT(*) AS count FROM outbox WHERE blocked=0")[0]?.count ?? 0);
  }

  cursor(): string | undefined {
    return rows(this.database.raw, "SELECT value FROM metadata WHERE key='sync_cursor'")[0]?.value as string | undefined;
  }

  async applySyncResult(acknowledged: string[], changes: ServerChange[], cursor?: string): Promise<void> {
    await this.database.transaction((db) => {
      for (const id of acknowledged) {
        const operation = rows(db, "SELECT entity_type,entity_id FROM outbox WHERE id=?", [id])[0];
        if (operation?.entity_type === "receipt") db.run("UPDATE sales SET synced_at=? WHERE id=?", [new Date().toISOString(), operation.entity_id]);
        db.run("DELETE FROM outbox WHERE id=?", [id]);
      }
      for (const change of changes) this.applyServerChange(db, change);
      if (cursor !== undefined) db.run("INSERT INTO metadata(key,value) VALUES('sync_cursor',?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [cursor]);
    });
  }

  async recordFailure(ids: string[], message: string): Promise<void> {
    await this.database.transaction((db) => {
      for (const id of ids) db.run("UPDATE outbox SET attempts=attempts+1,last_error=? WHERE id=?", [message.slice(0, 1000), id]);
    });
  }

  async blockRejected(id: string, reason: string): Promise<void> {
    await this.database.transaction((db) => {
      // Retain permanently rejected operations for support/audit without retrying them.
      db.run("UPDATE outbox SET attempts=attempts+1,last_error=?,blocked=1 WHERE id=?", [reason.slice(0, 1000), id]);
    });
  }

  private applyServerChange(db: SqlJsDatabase, change: ServerChange): void {
    // Catalog/config uses server revision wins. Sales remain immutable and are only acknowledged.
    if (change.entityType === "receipt") return;
    db.run("INSERT INTO server_entities(entity_type,entity_id,revision,updated_at,deleted,data) VALUES(?,?,?,?,?,?) ON CONFLICT(entity_type,entity_id) DO UPDATE SET revision=excluded.revision,updated_at=excluded.updated_at,deleted=excluded.deleted,data=excluded.data WHERE excluded.revision > server_entities.revision", [change.entityType, change.entityId, change.revision, change.updatedAt, change.deleted ? 1 : 0, change.data === undefined ? null : JSON.stringify(change.data)]);
  }
}
